import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react'
import cn from 'classnames'
import { useInView } from 'react-intersection-observer'
import useQuestChannel from 'state/channels/useQuestChannel'
import { dispatchLocalFeedTick } from 'state/feedTicks'
import isNewItemsEligible from 'components/panels/feedNewItemsEligibility'
import usePanelContext from 'refactor/hooks/usePanelContext'
import { useQuestImpression } from 'lib/questImpressions'
import {
    MIN_QUEST_GEOMETRY_HEIGHT,
    rememberRetainedQuestGeometry,
    resolveRetainedQuestGeometry,
    resolveInitialQuestGeometry,
    resolveQuestContainerStyle,
    resolveQuestGeometryRevision,
} from 'lib/questGeometryFrame'
import styles from 'components/panels/panel.module.scss'

const getElementBoxSize = element => {
    if (!element) return { height: 0, width: 0 }

    const rect = element.getBoundingClientRect?.()
    return {
        height: Math.max(element.offsetHeight || 0, rect?.height || 0),
        width: Math.max(element.clientWidth || 0, rect?.width || 0),
    }
}

const resolveVirtualQuestRowElement = element => {
    const row = element?.closest?.('[data-virtual-quest-row="true"]')
    return typeof HTMLElement !== 'undefined' && row instanceof HTMLElement ? row : null
}

const QUEST_CHANNEL_ROOT_MARGIN = '800px 0px 800px 0px'

export default function VirtualQuestInView({
    children,
    questId,
    quest = null,
    estimatedHeight = 0,
    estimatedWidth = 0,
    isThin = false,
    geometryKey = null,
    staticGeometry = false,
    naturalFlow = false,
    position = null,
}) {
    const containerRef = useRef(null)
    const geometryRevision = staticGeometry
        ? `static:${Math.ceil(Number(estimatedHeight || 0)) || 0}`
        : resolveQuestGeometryRevision(quest)
    const geometryIdentity = `${questId || 'unknown'}:${isThin ? 'thin' : 'stream'}:${
        geometryKey || 'default'
    }:${geometryRevision}:${staticGeometry ? 'static' : 'measured'}`
    const initialGeometry = resolveInitialQuestGeometry({
        questId,
        quest,
        estimatedHeight,
        estimatedWidth,
        isThin,
        geometryKey,
        useRememberedHeight: !staticGeometry,
    })
    const retainedHeightRef = useRef(initialGeometry.height)
    const lastMeasuredHeightRef = useRef(initialGeometry.height)
    const hasMeasuredHeightRef = useRef(initialGeometry.hasRememberedHeight)
    const geometryIdentityRef = useRef(geometryIdentity)

    if (geometryIdentityRef.current !== geometryIdentity) {
        geometryIdentityRef.current = geometryIdentity
        retainedHeightRef.current = initialGeometry.height
        lastMeasuredHeightRef.current = initialGeometry.height
        hasMeasuredHeightRef.current = initialGeometry.hasRememberedHeight
    }

    // TOP NEW seen-state: record that this card was actually shown (visible
    // past a dwell), so the unseen tier drains by scrolling, not by opening.
    // No-ops without the Top New capability. Panel `hide` uses visibility:hidden —
    // its boxes still intersect, hence the explicit disable. The same hook also
    // logs the exposure (dwell, surface, rank) for future ranking work.
    const { hide: panelHidden, panel, scrollContainerRef, filter } = usePanelContext()
    const deferFeedUpdates = isNewItemsEligible(panel?.filter, Boolean(gon?.currentUser))
    const deferQuestUpdate = useCallback(() => dispatchLocalFeedTick({ t: Date.now() }), [])
    const { subscribe, unsubscribe } = useQuestChannel(questId, {
        autoSubscribe: false,
        // Activity-ordered Home rows are immutable until the viewer crosses
        // the new-items pill. Missing preview hydration has its own bounded
        // queue in QuestStream. Other stream modes retain their old reconcile.
        reconcileOnSubscribe: !deferFeedUpdates,
        deferReconcileOnSubscribe: !deferFeedUpdates,
        deferQuestUpdates: deferFeedUpdates,
        onDeferredQuestUpdate: deferFeedUpdates ? deferQuestUpdate : undefined,
    })
    const { ref: visibilityRef, inView: channelVisible } = useInView({
        root: scrollContainerRef.current,
        rootMargin: QUEST_CHANNEL_ROOT_MARGIN,
        threshold: 0,
        skip: !!panelHidden,
    })

    useEffect(() => {
        if (panelHidden || !channelVisible) {
            unsubscribe()
            return
        }

        subscribe()
        return () => unsubscribe()
    }, [channelVisible, panelHidden, subscribe, unsubscribe])

    useQuestImpression({
        elementRef: containerRef,
        questId,
        spaceId: quest?.space_id,
        position,
        filter,
        isDraft: !!(quest?.is_draft || quest?.isLocalRootQuest || quest?.isLocalChildQuest),
        rootRef: scrollContainerRef,
        disabled: !!panelHidden,
    })

    const retainVirtualQuestRowGeometry = useCallback(() => {
        if (staticGeometry || naturalFlow) return

        const row = resolveVirtualQuestRowElement(containerRef.current)
        const measurement = getElementBoxSize(row)
        const nextHeight = resolveRetainedQuestGeometry({
            measuredHeight: measurement.height,
            previousHeight: retainedHeightRef.current,
            previousMeasuredHeight: lastMeasuredHeightRef.current,
            hasMeasuredHeight: hasMeasuredHeightRef.current,
            minHeight: MIN_QUEST_GEOMETRY_HEIGHT,
        })
        if (!nextHeight) return

        retainedHeightRef.current = nextHeight
        lastMeasuredHeightRef.current = nextHeight
        hasMeasuredHeightRef.current = true
        rememberRetainedQuestGeometry({
            questId,
            quest,
            height: nextHeight,
            width: measurement.width || estimatedWidth,
            isThin,
            geometryKey,
        })
    }, [estimatedWidth, geometryKey, isThin, naturalFlow, quest, questId, staticGeometry])

    useLayoutEffect(() => {
        if (staticGeometry || naturalFlow) return

        const row = resolveVirtualQuestRowElement(containerRef.current)
        if (!row) return

        let frameId = null
        let cancelFrame = null
        const scheduleMeasure = () => {
            if (frameId != null && cancelFrame) cancelFrame(frameId)
            const runMeasure = () => {
                frameId = null
                cancelFrame = null
                retainVirtualQuestRowGeometry()
            }

            if (typeof window.requestAnimationFrame === 'function') {
                frameId = window.requestAnimationFrame(runMeasure)
                cancelFrame = id => window.cancelAnimationFrame(id)
            } else {
                frameId = window.setTimeout(runMeasure, 0)
                cancelFrame = id => window.clearTimeout(id)
            }
        }

        scheduleMeasure()
        if (typeof ResizeObserver === 'undefined') {
            return () => {
                if (frameId != null && cancelFrame) cancelFrame(frameId)
            }
        }

        const observer = new ResizeObserver(scheduleMeasure)
        observer.observe(row)

        return () => {
            observer.disconnect()
            if (frameId != null && cancelFrame) cancelFrame(frameId)
        }
    }, [naturalFlow, retainVirtualQuestRowGeometry, staticGeometry])

    const containerStyle = naturalFlow
        ? undefined
        : resolveQuestContainerStyle({
              intrinsicHeight: retainedHeightRef.current,
              shouldRender: true,
              isVirtual: true,
          })
    const setContainerRef = useCallback(
        node => {
            containerRef.current = node
            visibilityRef(node)
        },
        [visibilityRef],
    )

    return (
        <div
            ref={setContainerRef}
            className={cn(styles.virtualQuestInView, `quest-in-view-${questId}`)}
            style={containerStyle}
        >
            {children}
        </div>
    )
}
